import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Activity, Zap, Coffee, Flame } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pulse colors and states
const PULSE_STATES = {
  high: {
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/30',
    icon: Flame,
    label: 'Fluxo Criativo Ativo',
  },
  medium: {
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    icon: Activity,
    label: 'Energia Diminuindo',
  },
  low: {
    color: 'text-red-400',
    bgColor: 'bg-red-400/10',
    borderColor: 'border-red-400/30',
    icon: Coffee,
    label: 'Pausa Recomendada',
  },
  neutral: {
    color: 'text-gray-400',
    bgColor: 'bg-gray-400/10',
    borderColor: 'border-gray-400/30',
    icon: Zap,
    label: 'Aguardando',
  },
};

/**
 * Pulso Criativo
 * Real-time creative energy indicator for the editor
 */
const PulsoCriativo = ({ 
  onPhraseChange, 
  wordsWritten = 0, 
  editsCount = 0,
  compact = false,
  showPhrase = true 
}) => {
  const [pulse, setPulse] = useState(null);
  const [phrase, setPhrase] = useState('');
  const [sessionActive, setSessionActive] = useState(false);

  // Start session on mount
  useEffect(() => {
    startSession();
    return () => {
      // Don't end session on unmount to preserve data
    };
  }, []);

  // Update session when writing stats change
  useEffect(() => {
    if (sessionActive && (wordsWritten > 0 || editsCount > 0)) {
      updateSession();
    }
  }, [wordsWritten, editsCount, sessionActive]);

  // Poll for pulse updates
  useEffect(() => {
    if (!sessionActive) return;
    
    const interval = setInterval(() => {
      getPulse();
    }, 30000); // Update every 30 seconds
    
    return () => clearInterval(interval);
  }, [sessionActive]);

  const startSession = async () => {
    try {
      await axios.post(`${API_URL}/api/creative/energy/start-session`, {}, {
        withCredentials: true
      });
      setSessionActive(true);
      getPulse();
    } catch (error) {
      console.error('Error starting session:', error);
    }
  };

  const updateSession = useCallback(async () => {
    try {
      const response = await axios.post(`${API_URL}/api/creative/energy/update`, {
        words_written: wordsWritten,
        edits_count: editsCount
      }, {
        withCredentials: true
      });
      
      if (response.data.energy) {
        setPulse(response.data.energy);
      }
      if (response.data.phrase) {
        setPhrase(response.data.phrase);
        onPhraseChange?.(response.data.phrase);
      }
    } catch (error) {
      console.error('Error updating session:', error);
    }
  }, [wordsWritten, editsCount, onPhraseChange]);

  const getPulse = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/creative/energy/pulse`, {
        withCredentials: true
      });
      
      setPulse(response.data.energy);
      setSessionActive(response.data.has_active_session);
      
      if (response.data.phrase) {
        setPhrase(response.data.phrase);
        onPhraseChange?.(response.data.phrase);
      }
    } catch (error) {
      console.error('Error getting pulse:', error);
    }
  };

  const state = pulse?.state || 'neutral';
  const pulseState = PULSE_STATES[state] || PULSE_STATES.neutral;
  const IconComponent = pulseState.icon;

  if (compact) {
    return (
      <div 
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${pulseState.bgColor} ${pulseState.borderColor} border transition-all`}
        title={pulseState.label}
      >
        <IconComponent size={14} className={`${pulseState.color} ${state === 'high' ? 'animate-pulse' : ''}`} />
        <div className={`w-2 h-2 rounded-full ${pulseState.color.replace('text-', 'bg-')} ${state === 'high' ? 'animate-pulse' : ''}`} />
      </div>
    );
  }

  return (
    <div className={`p-4 rounded-xl ${pulseState.bgColor} ${pulseState.borderColor} border transition-all`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full ${pulseState.bgColor} flex items-center justify-center`}>
          <IconComponent size={20} className={`${pulseState.color} ${state === 'high' ? 'animate-pulse' : ''}`} />
        </div>
        <div className="flex-1">
          <p className={`text-sm font-medium ${pulseState.color}`}>
            {pulseState.label}
          </p>
          {pulse?.score !== undefined && (
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 h-1.5 bg-white/30 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${pulseState.color.replace('text-', 'bg-')} transition-all duration-500`}
                  style={{ width: `${pulse.score}%` }}
                />
              </div>
              <span className="text-xs text-muted-foreground">{pulse.score}%</span>
            </div>
          )}
        </div>
      </div>

      {/* Inspirational Phrase */}
      {showPhrase && phrase && (
        <p className="mt-3 text-sm text-muted-foreground italic border-t border-current/10 pt-3">
          "{phrase}"
        </p>
      )}

      {/* Session Stats */}
      {pulse?.session_minutes > 0 && (
        <div className="mt-3 flex gap-4 text-xs text-muted-foreground">
          <span>{Math.round(pulse.session_minutes)} min escrevendo</span>
          {pulse.writing_rate > 0 && (
            <span>{pulse.writing_rate} palavras/min</span>
          )}
        </div>
      )}
    </div>
  );
};

export default PulsoCriativo;
